var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
var __accessCheck = (obj, member, msg) => {
  if (!member.has(obj))
    throw TypeError("Cannot " + msg);
};
var __privateGet = (obj, member, getter) => {
  __accessCheck(obj, member, "read from private field");
  return getter ? getter.call(obj) : member.get(obj);
};
var __privateAdd = (obj, member, value) => {
  if (member.has(obj))
    throw TypeError("Cannot add the same private member more than once");
  member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
};
var __privateSet = (obj, member, value, setter) => {
  __accessCheck(obj, member, "write to private field");
  setter ? setter.call(obj, value) : member.set(obj, value);
  return value;
};

// packages/remirror__extension-react-component/src/portals/portal-container.ts
import { createNanoEvents } from "nanoevents";
import { uniqueId } from "@remirror/core";
var _events;
var PortalContainer = class {
  constructor() {
    __publicField(this, "portals", /* @__PURE__ */ new Map());
    __privateAdd(this, _events, createNanoEvents());
    __publicField(this, "on", (callback) => {
      return __privateGet(this, _events).on("update", callback);
    });
    __publicField(this, "once", (callback) => {
      const unbind = __privateGet(this, _events).on("update", (portals) => {
        unbind();
        callback(portals);
      });
      return unbind;
    });
  }
  update() {
    __privateGet(this, _events).emit("update", this.portals);
  }
  render({ Component, container }) {
    var _a;
    const portal = this.portals.get(container);
    this.portals.set(container, { Component, key: (_a = portal == null ? void 0 : portal.key) != null ? _a : uniqueId() });
    this.update();
  }
  forceUpdate() {
    for (const [container, { Component }] of this.portals) {
      this.portals.set(container, { Component, key: uniqueId() });
    }
  }
  remove(container) {
    this.portals.delete(container);
    this.update();
  }
};
_events = new WeakMap();

// packages/remirror__extension-react-component/src/portals/react-portals.tsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
var RemirrorPortals = (props) => {
  const { portals } = props;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, portals.map(([container, { Component, key }]) => createPortal(/* @__PURE__ */ React.createElement(Component, null), container, key)));
};
function usePortals(portalContainer) {
  const [portals, setPortals] = useState(() => Array.from(portalContainer.portals.entries()));
  useEffect(() => {
    return portalContainer.on((portalMap) => {
      setPortals(Array.from(portalMap.entries()));
    });
  }, [portalContainer]);
  return useMemo(() => portals, [portals]);
}

// packages/remirror__extension-react-component/src/react-component-extension.ts
import {
  entries as entries2,
  extension,
  isNodeExtension,
  object,
  PlainExtension
} from "@remirror/core";

// packages/remirror__extension-react-component/src/react-node-view.tsx
import React2 from "react";
import {
  entries,
  ErrorConstant,
  invariant,
  isDomNode,
  isElementDomNode,
  isFunction,
  isNodeOfType,
  isPlainObject,
  isString,
  kebabCase,
  pascalCase,
  SELECTED_NODE_CLASS_NAME
} from "@remirror/core";
import { DOMSerializer } from "@remirror/pm/model";
import { NodeSelection } from "@remirror/pm/state";
var _node, _decorations, _view, _portalContainer, _Component, _getPosition, _options, _selected, _contentDOM, _contentDOMWrapper, _dom, _forwardRef;
var _ReactNodeView = class {
  constructor({
    getPosition,
    node,
    portalContainer,
    view,
    ReactComponent,
    options
  }) {
    __privateAdd(this, _node, void 0);
    __privateAdd(this, _decorations, []);
    __privateAdd(this, _view, void 0);
    __privateAdd(this, _portalContainer, void 0);
    __privateAdd(this, _Component, void 0);
    __privateAdd(this, _getPosition, void 0);
    __privateAdd(this, _options, void 0);
    __privateAdd(this, _selected, false);
    __privateAdd(this, _contentDOM, void 0);
    __privateAdd(this, _contentDOMWrapper, void 0);
    __privateAdd(this, _dom, void 0);
    __privateAdd(this, _forwardRef, (node) => {
      if (!node) {
        return;
      }
      invariant(__privateGet(this, _contentDOMWrapper), {
        code: ErrorConstant.REACT_NODE_VIEW,
        message: `You have applied a ref to a node view provided for '${__privateGet(this, _node).type.name}' which doesn't support content.`
      });
      node.append(__privateGet(this, _contentDOMWrapper));
    });
    __publicField(this, "Component", () => {
      const ReactComponent = __privateGet(this, _Component);
      invariant(ReactComponent, {
        code: ErrorConstant.REACT_NODE_VIEW,
        message: `The custom react node view provided for ${__privateGet(this, _node).type.name} doesn't have a valid ReactComponent`
      });
      return /* @__PURE__ */ React2.createElement(ReactComponent, {
        updateAttributes: this.updateAttributes,
        selected: this.selected,
        view: __privateGet(this, _view),
        getPosition: __privateGet(this, _getPosition),
        node: __privateGet(this, _node),
        forwardRef: __privateGet(this, _forwardRef),
        decorations: __privateGet(this, _decorations)
      });
    });
    __publicField(this, "updateAttributes", (attrs) => {
      if (!__privateGet(this, _view).editable) {
        return;
      }
      const tr = __privateGet(this, _view).state.tr.setNodeMarkup(__privateGet(this, _getPosition).call(this), void 0, {
        ...__privateGet(this, _node).attrs,
        ...attrs
      });
      __privateGet(this, _view).dispatch(tr);
    });
    var _a;
    invariant(isFunction(getPosition), {
      message: "You are attempting to use a node view for a mark type. This is not supported yet. Please check your configuration."
    });
    __privateSet(this, _node, node);
    __privateSet(this, _view, view);
    __privateSet(this, _portalContainer, portalContainer);
    __privateSet(this, _Component, ReactComponent);
    __privateSet(this, _getPosition, getPosition);
    __privateSet(this, _options, options);
    __privateSet(this, _dom, this.createDom());
    const { contentDOM, wrapper } = (_a = this.createContentDom()) != null ? _a : {};
    __privateSet(this, _contentDOM, contentDOM != null ? contentDOM : void 0);
    __privateSet(this, _contentDOMWrapper, wrapper);
    if (__privateGet(this, _contentDOMWrapper)) {
      __privateGet(this, _dom).append(__privateGet(this, _contentDOMWrapper));
    }
    this.setDomAttributes(__privateGet(this, _node), __privateGet(this, _dom));
    this.Component.displayName = pascalCase(`${__privateGet(this, _node).type.name}NodeView`);
    this.renderComponent();
  }
  static create(props) {
    const { portalContainer, ReactComponent, options } = props;
    return (node, view, getPosition) => new _ReactNodeView({
      options,
      node,
      view,
      getPosition,
      portalContainer,
      ReactComponent
    });
  }
  get selected() {
    return __privateGet(this, _selected);
  }
  get contentDOM() {
    return __privateGet(this, _contentDOM);
  }
  get dom() {
    return __privateGet(this, _dom);
  }
  renderComponent() {
    __privateGet(this, _portalContainer).render({
      Component: this.Component,
      container: __privateGet(this, _dom)
    });
  }
  createDom() {
    const { defaultBlockNode, defaultInlineNode } = __privateGet(this, _options);
    const element = __privateGet(this, _node).isInline ? document.createElement(defaultInlineNode) : document.createElement(defaultBlockNode);
    element.classList.add(`${kebabCase(__privateGet(this, _node).type.name)}-node-view-wrapper`);
    return element;
  }
  createContentDom() {
    var _a, _b;
    if (__privateGet(this, _node).isLeaf) {
      return;
    }
    const domSpec = (_b = (_a = __privateGet(this, _node).type.spec).toDOM) == null ? void 0 : _b.call(_a, __privateGet(this, _node));
    if (!domSpec) {
      return;
    }
    const { contentDOM, dom } = DOMSerializer.renderSpec(document, domSpec);
    let wrapper;
    if (!isElementDomNode(dom)) {
      return;
    }
    wrapper = dom;
    if (dom === contentDOM) {
      wrapper = document.createElement("span");
      wrapper.classList.add(`${kebabCase(__privateGet(this, _node).type.name)}-node-view-content-wrapper`);
      wrapper.append(contentDOM);
    }
    if (isElementDomNode(contentDOM)) {
    }
    return { wrapper, contentDOM };
  }
  update(node, decorations) {
    if (!isNodeOfType({ types: __privateGet(this, _node).type, node })) {
      return false;
    }
    if (__privateGet(this, _node) === node && __privateGet(this, _decorations) === decorations) {
      return true;
    }
    if (!__privateGet(this, _node).sameMarkup(node)) {
      this.setDomAttributes(node, __privateGet(this, _dom));
    }
    __privateSet(this, _node, node);
    __privateSet(this, _decorations, decorations);
    this.renderComponent();
    return true;
  }
  setDomAttributes(node, element) {
    const { toDOM } = __privateGet(this, _node).type.spec;
    let attributes = node.attrs;
    if (toDOM) {
      const domSpec = toDOM(node);
      if (isString(domSpec) || isDomNodeOutputSpec(domSpec)) {
        return;
      }
      if (isPlainObject(domSpec[1])) {
        attributes = domSpec[1];
      }
    }
    for (const [attribute, value] of entries(attributes)) {
      element.setAttribute(attribute, value);
    }
  }
  selectNode() {
    __privateSet(this, _selected, true);
    if (__privateGet(this, _dom)) {
      __privateGet(this, _dom).classList.add(SELECTED_NODE_CLASS_NAME);
    }
    this.renderComponent();
  }
  deselectNode() {
    __privateSet(this, _selected, false);
    if (__privateGet(this, _dom)) {
      __privateGet(this, _dom).classList.remove(SELECTED_NODE_CLASS_NAME);
    }
    this.renderComponent();
  }
  destroy() {
    __privateGet(this, _portalContainer).remove(__privateGet(this, _dom));
  }
  ignoreMutation(mutation) {
    if (mutation.type === "selection") {
      return !__privateGet(this, _node).type.spec.selectable;
    }
    if (!__privateGet(this, _contentDOMWrapper)) {
      return true;
    }
    return !__privateGet(this, _contentDOMWrapper).contains(mutation.target);
  }
  stopEvent(event) {
    var _a;
    if (!__privateGet(this, _dom)) {
      return false;
    }
    if (isFunction(__privateGet(this, _options).stopEvent)) {
      return __privateGet(this, _options).stopEvent({ event });
    }
    const target = event.target;
    const isInElement = __privateGet(this, _dom).contains(target) && !((_a = this.contentDOM) == null ? void 0 : _a.contains(target));
    if (!isInElement) {
      return false;
    }
    const isDropEvent = event.type === "drop";
    const isInput = ["INPUT", "BUTTON", "SELECT", "TEXTAREA"].includes(target.tagName) || target.isContentEditable;
    if (isInput && !isDropEvent) {
      return true;
    }
    const isDraggable = !!__privateGet(this, _node).type.spec.draggable;
    const isSelectable = NodeSelection.isSelectable(__privateGet(this, _node));
    const isCopyEvent = event.type === "copy";
    const isPasteEvent = event.type === "paste";
    const isCutEvent = event.type === "cut";
    const isClickEvent = event.type === "mousedown";
    const isDragEvent = event.type.startsWith("drag");
    if (!isDraggable && isSelectable && isDragEvent) {
      event.preventDefault();
    }
    if (isDragEvent || isDropEvent || isCopyEvent || isPasteEvent || isCutEvent || isClickEvent && isSelectable) {
      return false;
    }
    return true;
  }
};
var ReactNodeView = _ReactNodeView;
_node = new WeakMap();
_decorations = new WeakMap();
_view = new WeakMap();
_portalContainer = new WeakMap();
_Component = new WeakMap();
_getPosition = new WeakMap();
_options = new WeakMap();
_selected = new WeakMap();
_contentDOM = new WeakMap();
_contentDOMWrapper = new WeakMap();
_dom = new WeakMap();
_forwardRef = new WeakMap();
function isDomNodeOutputSpec(value) {
  return isDomNode(value) || isPlainObject(value) && isDomNode(value.dom);
}

// packages/remirror__extension-react-component/src/react-component-extension.ts
var ReactComponentExtension = class extends PlainExtension {
  constructor() {
    super(...arguments);
    __publicField(this, "portalContainer", new PortalContainer());
  }
  get name() {
    return "reactComponent";
  }
  onCreate() {
    this.store.setStoreKey("portalContainer", this.portalContainer);
  }
  createNodeViews() {
    var _a;
    const nodeViews = object();
    const managerComponents = (_a = this.store.managerSettings.nodeViewComponents) != null ? _a : {};
    for (const extension2 of this.store.extensions) {
      if (!extension2.ReactComponent || !isNodeExtension(extension2) || extension2.reactComponentEnvironment === "ssr") {
        continue;
      }
      nodeViews[extension2.name] = ReactNodeView.create({
        options: this.options,
        ReactComponent: extension2.ReactComponent,
        portalContainer: this.portalContainer
      });
    }
    const namedComponents = entries2({ ...this.options.nodeViewComponents, ...managerComponents });
    for (const [name, ReactComponent] of namedComponents) {
      nodeViews[name] = ReactNodeView.create({
        options: this.options,
        ReactComponent,
        portalContainer: this.portalContainer
      });
    }
    return nodeViews;
  }
};
ReactComponentExtension = __decorateClass([
  extension({
    defaultOptions: {
      defaultBlockNode: "div",
      defaultInlineNode: "span",
      defaultContentNode: "span",
      defaultEnvironment: "both",
      nodeViewComponents: {},
      stopEvent: null
    },
    staticKeys: ["defaultBlockNode", "defaultInlineNode", "defaultContentNode", "defaultEnvironment"]
  })
], ReactComponentExtension);
export {
  PortalContainer,
  ReactComponentExtension,
  RemirrorPortals,
  usePortals
};
//# sourceMappingURL=remirror-extension-react-component.js.map
